import { useHeyApi } from "@platform/api";
import { useQuery } from "@tanstack/react-query";

export interface NetworkOption {
  network: string;
  networkName?: string;
  label: string;
}

interface NetworkOptionsResponse {
  networkOptions: NetworkOption[];
}

interface UseNetworkOptionsOptions {
  rfpId: string | undefined;
  planOptionId: string | undefined;
  enabled?: boolean;
}

interface UseNetworkOptionsResult {
  networkOptions: NetworkOption[];
  isLoading: boolean;
  error: Error | null;
}

export const useNetworkOptions = ({
  rfpId,
  planOptionId,
  enabled = true,
}: UseNetworkOptionsOptions): UseNetworkOptionsResult => {
  const { centroPortalClient } = useHeyApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["quotes-network-options", rfpId, planOptionId],
    enabled: enabled && !!rfpId && !!planOptionId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!rfpId || !planOptionId) {
        return { networkOptions: [] } satisfies NetworkOptionsResponse;
      }

      const response = await centroPortalClient.get<
        NetworkOptionsResponse,
        unknown,
        true
      >({
        url: `/rfps/${encodeURIComponent(rfpId)}/plan-options/${encodeURIComponent(
          planOptionId,
        )}/network-options`,
        responseType: "json",
        throwOnError: true,
      });

      return response.data;
    },
  });

  return {
    networkOptions: data?.networkOptions ?? [],
    isLoading,
    error: error instanceof Error ? error : null,
  };
};
